import {DOCUMENT, inject, Injectable} from '@angular/core';
import {trackConsentChange, Tracker} from "./tracking";
import {TrackingEvent, TrackingEventType} from "./tracking-event";
import {ConsentService, CookieYesCategory} from "../services/consent.service";

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: Fbq;
  loaded: boolean;
  version: string;
  disablePushState?: boolean;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

/**
 * Lazily loads Meta Pixel. Calls made before {@link enable} (or after {@link disable}) are no-ops.
 */
@Injectable({
  providedIn: 'root'
})
export class MetaPixelService implements Tracker {

  private static readonly PIXEL_ID = '1087458049152150';
  private static readonly SCRIPT_URL = 'https://connect.facebook.net/en_US/fbevents.js';

  private readonly document = inject(DOCUMENT);
  private enabled = false;

  constructor() {
    const consentType = inject(ConsentService).signalFor(this.category());
    trackConsentChange(this, consentType);
  }

  private readonly eventMapping: Record<TrackingEventType, () => void> = {
    [TrackingEventType.SCHEDULE_INITIATED]: () => this.trackCustom('ScheduleInitiated'),
    [TrackingEventType.SCHEDULED]: () => this.track('Schedule'),
    [TrackingEventType.NAVIGATION_CHANGED]: () => {
    },
    [TrackingEventType.BLOG_SEARCHED]: () => {
    }
  };

  category(): CookieYesCategory {
    return 'advertisement';
  }

  public enable(): void {
    if (this.enabled) {
      return;
    }
    const window = this.document.defaultView!;
    if (window.fbq) {
      window.fbq('consent', 'grant');
    } else {
      this.load(window);
    }
    this.enabled = true;
  }

  public disable(): void {
    if (!this.enabled) {
      return;
    }
    this.document.defaultView!.fbq!('consent', 'revoke');
    this.enabled = false;
  }

  public trackPageView(_path: string): void {
    this.track('PageView');
  }

  public trackCustomEvent(event: TrackingEvent) {
    this.eventMapping[event.type]();
  }

  private track(eventType: string): void {
    this.call('track', eventType);
  }

  private trackCustom(eventType: string): void {
    this.call('trackCustom', eventType);
  }

  private call(...args: unknown[]): void {
    if (this.enabled) {
      this.document.defaultView!.fbq!(...args);
    }
  }

  /**
   * Equivalent of the official Meta Pixel base code snippet.
   */
  private load(window: Window): void {
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args);
      } else {
        fbq.queue.push(args);
      }
    } as Fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    // page views are tracked manually, only for routes with tracking enabled
    fbq.disablePushState = true;
    window.fbq = window._fbq = fbq;

    const script = this.document.createElement('script');
    script.async = true;
    script.src = MetaPixelService.SCRIPT_URL;
    this.document.head.appendChild(script);

    fbq('init', MetaPixelService.PIXEL_ID);
  }

}
